/**
 * useVoyageTemplates.js
 *
 * Hook for managing voyage templates (CRUD operations)
 * Templates pre-configure voyage settings for repeated routes
 *
 * PHASE 3: Voyage Template Builder
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { portLabel } from '../../../utils/voyage/portLabel';

/**
 * Generate unique template ID
 */
const generateTemplateId = () => {
  return `vt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Template data structure:
 * {
 *   id: string,
 *   name: string,
 *   description?: string,
 *   from: { name: string },
 *   to: { name: string },
 *   purpose?: string,
 *   checklists: Array<{ templateId: string, name: string, required: boolean }>,
 *   reportingPoints: Array<{ name: string, notes?: string }>,
 *   defaults: {
 *     expectedDurationHours?: number,
 *     typicalCrewCount?: number,
 *     typicalPassengerCount?: number,
 *     passengerBriefRequired?: boolean,
 *     notes?: string
 *   },
 *   createdAt: string,
 *   updatedAt: string,
 *   createdBy?: string,
 *   usageCount: number,
 *   lastUsed?: string
 * }
 */

const useVoyageTemplates = () => {
  // FIXED: useStorageState returns { value, setValue }, NOT [value, setValue]
  const { value: rawTemplates, setValue: setTemplates } = useStorageState('operations_voyage_templates', []);

  // Ensure templates is always an array (safety check for null/undefined from storage)
  const templates = Array.isArray(rawTemplates) ? rawTemplates : [];

  /**
   * Create a new template
   */
  const createTemplate = useCallback((templateData) => {
    const now = new Date().toISOString();
    const newTemplate = {
      id: generateTemplateId(),
      name: templateData.name || 'Untitled Template',
      description: templateData.description || '',
      from: templateData.from || { name: '' },
      to: templateData.to || { name: '' },
      purpose: templateData.purpose || '',
      checklists: templateData.checklists || [],
      reportingPoints: templateData.reportingPoints || [],
      defaults: {
        expectedDurationHours: templateData.defaults?.expectedDurationHours || null,
        typicalCrewCount: templateData.defaults?.typicalCrewCount || null,
        typicalPassengerCount: templateData.defaults?.typicalPassengerCount || null,
        passengerBriefRequired: templateData.defaults?.passengerBriefRequired ?? true,
        notes: templateData.defaults?.notes || ''
      },
      createdAt: now,
      updatedAt: now,
      createdBy: templateData.createdBy || '',
      usageCount: 0,
      lastUsed: null
    };

    setTemplates(prev => [...(Array.isArray(prev) ? prev : []), newTemplate]);
    return newTemplate;
  }, [setTemplates]);

  /**
   * Create template from an existing voyage
   */
  const createFromVoyage = useCallback((voyage, templateName) => {
    const templateData = {
      name: templateName || `${portLabel(voyage.from)} to ${portLabel(voyage.to)}`,
      description: `Created from voyage ${voyage.voyageNumber || voyage.id}`,
      from: voyage.from,
      to: voyage.to,
      purpose: voyage.purpose || '',
      checklists: voyage.checklists || [],
      reportingPoints: voyage.reportingPoints || [],
      defaults: {
        expectedDurationHours: voyage.expectedDurationHours || null,
        typicalCrewCount: voyage.crewCount || null,
        typicalPassengerCount: voyage.passengerCount || null,
        passengerBriefRequired: voyage.passengerBriefRequired ?? true,
        notes: voyage.notes || ''
      }
    };

    return createTemplate(templateData);
  }, [createTemplate]);

  /**
   * Update an existing template
   */
  const updateTemplate = useCallback((templateId, updates) => {
    setTemplates(prev => (Array.isArray(prev) ? prev : []).map(t => {
      if (t.id === templateId) {
        return {
          ...t,
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
      return t;
    }));
  }, [setTemplates]);

  /**
   * Delete a template
   */
  const deleteTemplate = useCallback((templateId) => {
    setTemplates(prev => (Array.isArray(prev) ? prev : []).filter(t => t.id !== templateId));
  }, [setTemplates]);

  /**
   * Get a specific template by ID
   */
  const getTemplate = useCallback((templateId) => {
    return templates.find(t => t.id === templateId) || null;
  }, [templates]);

  /**
   * Increment usage count and update lastUsed
   */
  const incrementUsage = useCallback((templateId) => {
    setTemplates(prev => (Array.isArray(prev) ? prev : []).map(t => {
      if (t.id === templateId) {
        return {
          ...t,
          usageCount: (t.usageCount || 0) + 1,
          lastUsed: new Date().toISOString()
        };
      }
      return t;
    }));
  }, [setTemplates]);

  /**
   * Get templates sorted by usage (most used first)
   */
  const templatesByUsage = useMemo(() => {
    return [...templates].sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
  }, [templates]);

  /**
   * Get recent templates (used in last 30 days)
   */
  const recentTemplates = useMemo(() => {
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return templates
      .filter(t => t.lastUsed && new Date(t.lastUsed).getTime() > thirtyDaysAgo)
      .sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime());
  }, [templates]);

  /**
   * Apply a template to create voyage data
   */
  const applyTemplate = useCallback((templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return null;

    // Increment usage
    incrementUsage(templateId);

    // Return voyage-ready data
    return {
      from: template.from,
      to: template.to,
      purpose: template.purpose,
      checklists: template.checklists,
      reportingPoints: template.reportingPoints,
      expectedDurationHours: template.defaults?.expectedDurationHours,
      crewCount: template.defaults?.typicalCrewCount,
      passengerCount: template.defaults?.typicalPassengerCount,
      passengerBriefRequired: template.defaults?.passengerBriefRequired,
      notes: template.defaults?.notes,
      _fromTemplate: templateId
    };
  }, [templates, incrementUsage]);

  return {
    templates,
    templatesByUsage,
    recentTemplates,
    createTemplate,
    createFromVoyage,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    incrementUsage,
    applyTemplate
  };
};

export default useVoyageTemplates;
